import { useTokenStore } from "@/stores/token.store";
import { axiosInstance } from "./axiosInstance";
import { REFRESH_BUFFER_SECONDS } from "./constants";
import { ApiError } from "./handleError";

// Define the expected JWT payload structure
interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

/**
 * Decodes a JWT token's payload safely.
 */
export const decodeToken = (token: string): JwtPayload | null => {
  try {
    if (!token || token.split(".").length !== 3) {
      return null;
    }

    const payload = token.split(".")[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);

    // TODO: Get the full type
    const decoded = JSON.parse(atob(base64 + padding)) as JwtPayload;

    return decoded;
  } catch (error) {
    console.warn("Failed to decode token:", error);
    return null;
  }
};

/**
 * Checks if the token is expired or nearing expiration (within buffer).
 */
export const isTokenExpired = (
  token: string | null,
  bufferSeconds: number = REFRESH_BUFFER_SECONDS
): boolean => {
  if (!token) {
    return true;
  }

  const decoded = decodeToken(token);

  if (!decoded || !decoded.exp) {
    return true;
  }

  const expirationDate = new Date(decoded.exp * 1000);
  const now = new Date();
  const bufferMillis = bufferSeconds * 1000;

  return expirationDate.getTime() <= now.getTime() + bufferMillis;
};

/**
 * Performs the token refresh request.
 */
export const performTokenRefresh = async (
  tokenStore: ReturnType<typeof useTokenStore>
): Promise<string | null> => {
  try {
    const response = await axiosInstance.post<{ token: string }>(
      "/api/auth/token/refresh/"
    );

    if (!response.data?.token) {
      throw new ApiError({
        message: "Invalid refresh token",
        success: false,
        code: "INVALID_REFRESH_TOKEN",
      });
    }

    const newToken = response.data.token;

    tokenStore.$patch({ token: newToken });

    return newToken;
  } catch (error) {
    console.error("Token refresh failed:", error);

    tokenStore.$patch({ token: null, refreshToken: null });
    return null;
  }
};
